import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageCircle, FileText, Wrench, Receipt,
  Clock, Phone, Shield, ChevronRight, CheckCircle,
  ArrowRight, Building2, Mail, MapPin, Loader2
} from 'lucide-react';

const MODULES = [
  {
    icon: MessageCircle,
    title: 'Anliegen digital einreichen',
    text: 'Reparaturwünsche, Fragen und Mitteilungen strukturiert übermitteln — kein Zettel, kein Telefonspiel.',
    color: 'indigo',
  },
  {
    icon: Wrench,
    title: 'Mängel mit Fotos melden',
    text: 'Schäden direkt mit Bild dokumentieren. Klarer Nachweis für beide Seiten, schnellere Bearbeitung.',
    color: 'violet',
  },
  {
    icon: FileText,
    title: 'Dokumente abrufen',
    text: 'Mietvertrag, Hausordnung, Protokolle — jederzeit verfügbar, ohne Nachfragen.',
    color: 'blue',
  },
  {
    icon: Receipt,
    title: 'Nebenkosten nachvollziehen',
    text: 'Abrechnungen verständlicher aufbereitet. Keine Überraschungen, mehr Transparenz.',
    color: 'teal',
  },
  {
    icon: Clock,
    title: 'Status von Anfragen sehen',
    text: 'Wann wurde mein Anliegen bearbeitet? Clara360 zeigt den aktuellen Stand — ohne Nachfassen.',
    color: 'cyan',
  },
  {
    icon: Phone,
    title: 'Kommunikation per Portal oder WhatsApp',
    text: 'Nachrichten über den Kanal, den Sie bevorzugen. Alles dokumentiert, nichts geht verloren.',
    color: 'emerald',
  },
];

const colorMap = {
  indigo: { bg: 'rgba(99,102,241,0.12)', border: 'rgba(99,102,241,0.25)', icon: '#818cf8' },
  violet: { bg: 'rgba(139,92,246,0.12)', border: 'rgba(139,92,246,0.25)', icon: '#a78bfa' },
  blue:   { bg: 'rgba(59,130,246,0.12)',  border: 'rgba(59,130,246,0.25)',  icon: '#60a5fa' },
  teal:   { bg: 'rgba(20,184,166,0.12)',  border: 'rgba(20,184,166,0.25)',  icon: '#2dd4bf' },
  cyan:   { bg: 'rgba(6,182,212,0.12)',   border: 'rgba(6,182,212,0.25)',   icon: '#22d3ee' },
  emerald:{ bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.25)', icon: '#34d399' },
};

const MieterPage = () => {
  const [showForm, setShowForm] = useState(false);
  const [step, setStep] = useState('form');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '', email: '', city: '', landlord: '', message: '',
    website: '',
  });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name || 'Mieter (anonym)',
          email: form.email || `anon_${Date.now()}@mieter.clara360.de`,
          phone: null,
          company: form.landlord || null,
          role: 'Mieter',
          units: null,
          current_software: null,
          main_problem: form.message || null,
          desired_next_step: 'Interesse vormerken',
          consent: true,
          source: 'tenant_page',   // ← sauber getrennt von pilot_page
          website: form.website,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      setStep('success');
    } catch (err) {
      console.error('Mieter-Lead error:', err.message);
      setError('Etwas ist schiefgelaufen. Schreiben Sie uns direkt per WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = "w-full px-4 py-3 rounded-lg text-sm border focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 transition-colors";
  const inputStyle = {
    background: 'rgba(255,255,255,0.08)',
    borderColor: 'rgba(255,255,255,0.15)',
    color: '#e2e8f0',
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 35%, #1e3a5f 65%, #1e293b 100%)',
      }}
    >
      {/* Header */}
      <header
        className="sticky top-0 z-50 flex items-center justify-between px-6 py-3"
        style={{
          background: 'rgba(15,23,42,0.85)',
          backdropFilter: 'blur(16px)',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
        }}
      >
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/clara360-logo.png" alt="Clara360" className="h-7 w-auto" />
          <span className="text-white font-semibold text-base tracking-tight">Clara360</span>
        </Link>
        <div className="flex items-center gap-3">
          <Link
            to="/pilot"
            className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium text-indigo-300 transition-colors hover:text-white"
            style={{ background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)' }}
          >
            Für Verwaltungen
          </Link>
          <Link
            to="/login"
            className="px-4 py-2 rounded-lg text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-500 transition-colors"
          >
            Anmelden
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative flex flex-col items-center text-center px-6 pt-20 pb-16 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/8 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-indigo-500/8 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-2xl mx-auto">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-6"
            style={{
              background: 'rgba(59,130,246,0.12)',
              border: '1px solid rgba(59,130,246,0.3)',
              color: '#93c5fd',
            }}
          >
            <Shield className="w-3 h-3" />
            Frühankündigung — Clara360 für Mieter
          </div>
          <h1 className="text-white font-bold text-3xl md:text-4xl leading-tight mb-4">
            Mehr Transparenz.<br />
            Schnellere Rückmeldungen.<br />
            <span style={{ color: '#60a5fa' }}>Digitale Verwaltung für Mieter.</span>
          </h1>
          <p className="text-slate-300 text-base leading-relaxed mb-8 max-w-xl mx-auto">
            Mit Clara360 können Mieter Anliegen strukturierter einreichen, Dokumente abrufen
            und Rückmeldungen nachvollziehen — sofern Eigentümer oder Hausverwaltung Clara360 einsetzen.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 transition-colors"
            >
              <ArrowRight className="w-4 h-4" />
              Interesse vormerken
            </button>
            <a
              href="https://wa.me/491709782676?text=Hallo%20Clara360%2C%20ich%20bin%20Mieter%20und%20m%C3%B6chte%20mehr%20erfahren."
              target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium text-green-300 transition-colors"
              style={{ background: 'rgba(37,211,102,0.10)', border: '1px solid rgba(37,211,102,0.25)' }}
            >
              <MessageCircle className="w-4 h-4" />
              Per WhatsApp anfragen
            </a>
          </div>
        </div>
      </section>

      {/* Hinweis-Block */}
      <section className="px-6 pb-10">
        <div
          className="max-w-2xl mx-auto rounded-xl px-5 py-4 flex items-start gap-3"
          style={{
            background: 'rgba(59,130,246,0.08)',
            border: '1px solid rgba(59,130,246,0.2)',
          }}
        >
          <Shield className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
          <p className="text-slate-300 text-sm leading-relaxed">
            <span className="text-blue-300 font-medium">Wichtig:</span>{' '}
            Clara360 kann nur genutzt werden, wenn Ihre Hausverwaltung oder Ihr Eigentümer
            Clara360 einsetzt. Wir beraten keine Mietrechtsfragen und geben keine Rechtsauskunft.
          </p>
        </div>
      </section>

      {/* Nutzenmodule */}
      <section className="px-6 pb-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-white font-bold text-xl md:text-2xl mb-2">
              Was Clara360 für Mieter bedeutet
            </h2>
            <p className="text-slate-400 text-sm">
              Sobald Ihre Verwaltung Clara360 nutzt, stehen Ihnen diese Funktionen zur Verfügung.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {MODULES.map((mod) => {
              const c = colorMap[mod.color];
              const Icon = mod.icon;
              return (
                <div
                  key={mod.title}
                  className="rounded-xl p-5 transition-all duration-200 hover:-translate-y-0.5"
                  style={{ background: c.bg, border: `1px solid ${c.border}` }}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
                    style={{ background: c.bg, border: `1px solid ${c.border}` }}
                  >
                    <Icon className="w-4 h-4" style={{ color: c.icon }} />
                  </div>
                  <h3 className="text-white font-semibold text-sm mb-1.5">{mod.title}</h3>
                  <p className="text-slate-400 text-xs leading-relaxed">{mod.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Empfehlung CTA */}
      <section className="px-6 pb-16">
        <div
          className="max-w-2xl mx-auto rounded-2xl p-7 text-center"
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.09)',
          }}
        >
          <Building2 className="w-8 h-8 text-indigo-400 mx-auto mb-4" />
          <h2 className="text-white font-bold text-lg mb-2">
            Clara360 meiner Verwaltung empfehlen
          </h2>
          <p className="text-slate-400 text-sm leading-relaxed mb-5">
            Wenn Sie möchten, dass Ihre Hausverwaltung oder Ihr Eigentümer Clara360 einsetzt,
            leiten Sie ihnen einfach den Link weiter.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/pilot"
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
              Zur Verwaltungs-Seite
            </Link>
            <button
              onClick={() => {
                const url = window.location.origin + '/pilot';
                navigator.clipboard?.writeText(url).then(() => {
                  alert('Link kopiert: ' + url);
                }).catch(() => {
                  prompt('Link zum Teilen:', url);
                });
              }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-slate-300 transition-colors"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}
            >
              Link kopieren
            </button>
          </div>
        </div>
      </section>

      {/* Interesse-Formular */}
      {showForm && (
        <section className="px-6 pb-16">
          <div className="max-w-lg mx-auto">
            <div
              className="rounded-2xl p-7"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.10)',
                backdropFilter: 'blur(16px)',
              }}
            >
              {step === 'success' ? (
                <div className="text-center py-4">
                  <div className="w-12 h-12 rounded-full bg-indigo-500/20 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-6 h-6 text-indigo-400" />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">Interesse vorgemerkt</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    Wir nehmen Ihr Interesse zur Kenntnis. Sobald Clara360 in Ihrer Region
                    verfügbar ist oder Ihre Verwaltung einsteigt, melden wir uns.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="text-white font-semibold text-base mb-1">Interesse vormerken</h3>
                  <p className="text-slate-400 text-xs mb-5">
                    Alle Felder optional. Wir speichern Ihr Interesse — keine Werbung, kein Spam.
                  </p>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <input
                      type="text" name="website" value={form.website}
                      onChange={handleChange} tabIndex={-1} autoComplete="off"
                      aria-hidden="true"
                      style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1.5">Name <span className="text-slate-600">(optional)</span></label>
                        <input
                          type="text" name="name" value={form.name}
                          onChange={handleChange} placeholder="Ihr Name"
                          className={inputClass} style={inputStyle}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1.5">E-Mail <span className="text-slate-600">(optional)</span></label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                          <input
                            type="email" name="email" value={form.email}
                            onChange={handleChange} placeholder="ihre@email.de"
                            className={inputClass + " pl-8"} style={inputStyle}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1.5">Stadt <span className="text-slate-600">(optional)</span></label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                          <input
                            type="text" name="city" value={form.city}
                            onChange={handleChange} placeholder="z.B. München"
                            className={inputClass + " pl-8"} style={inputStyle}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1.5">Hausverwaltung <span className="text-slate-600">(optional)</span></label>
                        <div className="relative">
                          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                          <input
                            type="text" name="landlord" value={form.landlord}
                            onChange={handleChange} placeholder="Name der Verwaltung"
                            className={inputClass + " pl-8"} style={inputStyle}
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1.5">Nachricht <span className="text-slate-600">(optional)</span></label>
                      <textarea
                        name="message" value={form.message} onChange={handleChange}
                        placeholder="Was wünschen Sie sich von Clara360?"
                        rows={3}
                        className={inputClass + " resize-none"} style={inputStyle}
                      />
                    </div>
                    {error && (
                      <div className="rounded-lg p-3 bg-red-500/10 border border-red-500/25">
                        <p className="text-red-400 text-xs">{error}</p>
                      </div>
                    )}
                    <button
                      type="submit" disabled={isSubmitting}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 transition-colors"
                    >
                      {isSubmitting
                        ? <><Loader2 className="w-4 h-4 animate-spin" />Wird gespeichert...</>
                        : <><CheckCircle className="w-4 h-4" />Interesse vormerken</>
                      }
                    </button>
                    <p className="text-slate-600 text-xs text-center">
                      Keine Werbung. Keine Weitergabe. Nur Benachrichtigung bei Verfügbarkeit.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer
        className="mt-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3"
        style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div className="flex items-center gap-2">
          <Shield className="w-3.5 h-3.5 text-slate-600" />
          <p className="text-slate-600 text-xs">
            Keine Rechts- oder Steuerberatung · Keine Aktion ohne Freigabe · Lokal bevorzugt
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-600">
          <Link to="/pilot" className="hover:text-slate-400 transition-colors">Für Verwaltungen</Link>
          <Link to="/login" className="hover:text-slate-400 transition-colors">Anmelden</Link>
        </div>
      </footer>
    </div>
  );
};

export default MieterPage;
